import React, { useCallback, useEffect, useState } from 'react';
import * as Styles from './PurchaseTrial.styles';
import { PaymentMethods } from './PurchaseTrial.types';
import {
  Button, IconAiStar, IconPresent, IconNotification, IconSecurityV2,
} from 'gatorcomponents';
import { RadioBlock } from './components/RadioBlock';
import { TrialTimeline } from './components/TrialTimeline';
import { useResize } from '@/hooks/useResize';
import { formatCurrency } from '@/utils/Formatters/formatCurrency';

const PurchaseTrial = ({
  locales,
  redirectToCheckout,
  handleCloseModal,
  purchaseTrialWithoutCc,
  productTrial,
  productPro,
  discount,
  provisionFreePlan,
  fastcheckoutLoading,
}) => {
  useEffect(() => {
    provisionFreePlan && provisionFreePlan();
  }, [provisionFreePlan]);

  const [paymentOption, setPaymentOption] = useState(null);

  const windowRect = useResize();
  const { width } = windowRect;
  const mobile = width <= 600;

  const mountPaymentOptionsList = useCallback(() => {
    const list = [];

    if (discount) {
      list.push({
        id: PaymentMethods.CREDIT_CARD,
        titleLabel: locales.options.creditCard.title,
        discountLabel: locales.options.creditCard.discount(discount.discount),
        pid: productTrial && productTrial.pid,
        priceLabel: locales.options.creditCard.price(formatCurrency(discount.price)),
        bottomTextLabel: locales.options.creditCard.bottomText,
      });
    }

    if (productTrial) {
      list.push({
        id: PaymentMethods.NO_CREDIT_CARD,
        titleLabel: locales.options.noCreditCard.title,
        descriptionLabel: locales.options.noCreditCard.description,
        pid: productTrial && productTrial.pid,
        priceLabel: productPro && locales.options.noCreditCard.price(formatCurrency(productPro.price)),
      });
    }

    return list;
  }, [locales, discount, productTrial, productPro]);

  const paymentOptionsList = mountPaymentOptionsList();

  const timelineItemsList = [
    {
      testId: 'startOfTest',
      variant: 'gradientBlue',
      icon: <IconSecurityV2 color="primaryUltralight" size="sm" />,
      title: locales.timeline.startOfTest.title,
      description: locales.timeline.startOfTest.description,
    },
    {
      testId: 'reminderOfTest',
      variant: 'white',
      icon: <IconNotification color="primaryMedium" />,
      title: locales.timeline.reminderOfTest.title,
      description: locales.timeline.reminderOfTest.description,
    },
    {
      testId: 'endOfTest',
      variant: 'white',
      icon: (
        <Styles.IconAiStarContainer>
          <IconAiStar color="primaryMedium" />
        </Styles.IconAiStarContainer>
      ),
      title: locales.timeline.endOfTest.title,
      description: discount
        && discount.price
        && locales.timeline.endOfTest.description(formatCurrency(discount.price)),
    },
  ];

  const handleClose = () => { handleCloseModal && handleCloseModal(); };

  const handleRedirectToPLG = () => {
    const { pid } = paymentOptionsList.find(option => option.id === paymentOption);

    redirectToCheckout && redirectToCheckout({ pid });
  };

  const handleContinue = () => {
    if (paymentOption === PaymentMethods.CREDIT_CARD) {
      handleRedirectToPLG();
    }
    if (paymentOption === PaymentMethods.NO_CREDIT_CARD) {
      purchaseTrialWithoutCc && purchaseTrialWithoutCc();
    }
  };

  return (
    <Styles.BackgroundWrapper>
      <Styles.Wrapper>
        <Styles.ModalTitle>{locales.title}</Styles.ModalTitle>

        <Styles.ModalContent>
          <Styles.PurchaseActionsContainer>
            {discount && discount.discount && (
              <Styles.ModalDescription>
                {locales.description(discount.discount)}
              </Styles.ModalDescription>
            )}

            <Styles.RadioBlocksContainer>
              {paymentOptionsList.map(option => (
                <RadioBlock
                  key={option.id}
                  optionId={option.id}
                  onClick={() => setPaymentOption(option.id)}
                  selected={paymentOption === option.id}
                  titleLabel={option.titleLabel}
                  descriptionLabel={option.descriptionLabel}
                  discountLabel={option.discountLabel}
                  priceLabel={option.priceLabel}
                  bottomTextLabel={option.bottomTextLabel}
                />
              ))}

              <Styles.ButtonsContainer>
                <Button
                  testId="handle-cancel-button"
                  label={locales.cancelButtonLabel}
                  onClick={handleClose}
                  version="v2"
                  variant="tertiary"
                  size="medium2"
                />

                <Button
                  testId="handle-continue-button"
                  label={locales.continueButtonLabel}
                  onClick={handleContinue}
                  version="v2"
                  size="medium2"
                  disabled={!paymentOption || fastcheckoutLoading}
                  loading={fastcheckoutLoading}
                />
              </Styles.ButtonsContainer>

              {!mobile && <Styles.BottomText>{locales.bottomMessage}</Styles.BottomText>}
            </Styles.RadioBlocksContainer>
          </Styles.PurchaseActionsContainer>

          <Styles.ExplanationContainer>
            <Styles.ExplanationTitleContainer>
              <IconPresent color="primaryMedium" size="sm" />
              <Styles.ExplanationTitle>{locales.howTestWorks}</Styles.ExplanationTitle>
            </Styles.ExplanationTitleContainer>

            <TrialTimeline timelineItemsList={timelineItemsList} />

            <Styles.TimelineBottomText>{locales.timeline.bottomText}</Styles.TimelineBottomText>
          </Styles.ExplanationContainer>

          {mobile && <Styles.BottomText>{locales.bottomMessage}</Styles.BottomText>}
        </Styles.ModalContent>
      </Styles.Wrapper>
    </Styles.BackgroundWrapper>
  );
};

export default PurchaseTrial;
